import React, { useState, useEffect, useRef } from "react";
import { Calendar, ChevronDown, Check, X } from "lucide-react";

export type DatePresetId = "last30d" | "last3m" | "last6m" | "ytd" | "last12m" | "custom";

export interface DateRangeValue {
  startDate: Date;
  endDate: Date;
  preset: DatePresetId;
}

export const PRESET_OPTIONS: { id: DatePresetId; label: string }[] = [
  { id: "last30d", label: "Last 30 Days" },
  { id: "last3m", label: "Last 3 Months" },
  { id: "last6m", label: "Last 6 Months" },
  { id: "ytd", label: "Year to Date" },
  { id: "last12m", label: "Last 12 Months" },
];

/** Format Date as "01 May 2026" */
export const formatDateDisplay = (d: Date): string => {
  if (!d || isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

/** Format Date as "01/05/2026" (DD/MM/YYYY) */
export const formatDateSlash = (d: Date): string => {
  if (!d || isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/** Format Date as "YYYY-MM-DD" for HTML input type="date" */
export const formatYmd = (d: Date): string => {
  if (!d || isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Format Date Range as "01 May 2026 — 09 Oct 2026" */
export const formatDateRangeLabel = (start: Date, end: Date): string => {
  return `${formatDateDisplay(start)} \u2014 ${formatDateDisplay(end)}`;
};

/** Calculate start and end date for a preset */
export function getPresetDates(
  presetId: DatePresetId,
  ref: Date = new Date()
): { startDate: Date; endDate: Date } {
  const end = new Date(ref);
  end.setHours(23, 59, 59, 999);

  let start = new Date(ref);
  start.setHours(0, 0, 0, 0);

  switch (presetId) {
    case "last30d":
      start.setDate(start.getDate() - 29);
      break;
    case "last3m":
      // 1st of month 2 months ago (total 3 months window up to current month)
      start = new Date(ref.getFullYear(), ref.getMonth() - 2, 1, 0, 0, 0, 0);
      break;
    case "last6m":
      // 1st of month 5 months ago (total 6 months window up to current month)
      start = new Date(ref.getFullYear(), ref.getMonth() - 5, 1, 0, 0, 0, 0);
      break;
    case "ytd":
      // Jan 1st of current year
      start = new Date(ref.getFullYear(), 0, 1, 0, 0, 0, 0);
      break;
    case "last12m":
      // 1st of month 11 months ago (total 12 months window)
      start = new Date(ref.getFullYear(), ref.getMonth() - 11, 1, 0, 0, 0, 0);
      break;
    default:
      start = new Date(ref.getFullYear(), ref.getMonth() - 5, 1, 0, 0, 0, 0);
  }

  return { startDate: start, endDate: end };
}

export interface DateRangeFilterProps {
  value?: DateRangeValue;
  defaultValue?: DateRangeValue;
  onChange?: (range: DateRangeValue) => void;
  className?: string;
  buttonId?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  defaultValue,
  onChange,
  className = "",
  buttonId,
}) => {
  // Initial default: Last 6 Months (as in screenshot)
  const [internalRange, setInternalRange] = useState<DateRangeValue>(() => {
    if (defaultValue) return defaultValue;
    const { startDate, endDate } = getPresetDates("last6m");
    return { startDate, endDate, preset: "last6m" };
  });

  const activeRange = value ?? internalRange;

  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState<Date>(activeRange.startDate);
  const [tempEnd, setTempEnd] = useState<Date>(activeRange.endDate);
  const [tempPreset, setTempPreset] = useState<DatePresetId>(activeRange.preset);

  const containerRef = useRef<HTMLDivElement>(null);

  // Sync internal temp state when activeRange changes or dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTempStart(activeRange.startDate);
      setTempEnd(activeRange.endDate);
      setTempPreset(activeRange.preset);
    }
  }, [isOpen, activeRange]);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const handlePresetClick = (presetId: DatePresetId) => {
    const { startDate, endDate } = getPresetDates(presetId);
    setTempStart(startDate);
    setTempEnd(endDate);
    setTempPreset(presetId);
  };

  const handleStartDateChange = (valStr: string) => {
    if (!valStr) return;
    const [y, m, d] = valStr.split("-").map(Number);
    const newDate = new Date(y, m - 1, d, 0, 0, 0, 0);
    setTempStart(newDate);
    setTempPreset("custom");
  };

  const handleEndDateChange = (valStr: string) => {
    if (!valStr) return;
    const [y, m, d] = valStr.split("-").map(Number);
    const newDate = new Date(y, m - 1, d, 23, 59, 59, 999);
    setTempEnd(newDate);
    setTempPreset("custom");
  };

  const handleApply = () => {
    let finalStart = tempStart;
    let finalEnd = tempEnd;
    if (finalStart > finalEnd) {
      finalStart = tempEnd;
      finalEnd = tempStart;
    }
    const newRange: DateRangeValue = {
      startDate: finalStart,
      endDate: finalEnd,
      preset: tempPreset,
    };
    if (!value) {
      setInternalRange(newRange);
    }
    onChange?.(newRange);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempStart(activeRange.startDate);
    setTempEnd(activeRange.endDate);
    setTempPreset(activeRange.preset);
    setIsOpen(false);
  };

  return (
    <div className={`relative inline-block ${className}`} ref={containerRef}>
      {/* ── Trigger Button (Pill with soft teal border) ── */}
      <button
        type="button"
        id={buttonId}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="inline-flex items-center gap-2 rounded-full border border-[#99E6DF] bg-white px-3.5 py-1.5 text-xs font-semibold text-[#10253A] shadow-sm hover:border-[#15BBAE] transition-all cursor-pointer select-none"
      >
        <Calendar size={15} className="text-[#15BBAE] stroke-[2.2]" aria-hidden="true" />
        <span className="font-semibold text-xs sm:text-[13px] text-[#10253A]">
          {formatDateRangeLabel(activeRange.startDate, activeRange.endDate)}
        </span>
        <ChevronDown
          size={14}
          className={`text-[#63788E] transition-transform duration-200 ${
            isOpen ? "rotate-180 text-[#15BBAE]" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {/* ── Dropdown Popover ── */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="Select Date Range"
          className="absolute right-0 top-full mt-2 z-50 w-[340px] sm:w-[370px] rounded-2xl border border-[#E4EAF0] bg-white p-4 shadow-xl animate-in fade-in zoom-in-95"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-[#F0F4F8]">
            <div className="flex items-center gap-2 text-[#10253A]">
              <Calendar size={17} className="text-[#15BBAE] stroke-[2.2]" />
              <span className="text-[14px] font-bold text-[#10253A]">Select Date Range</span>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-md p-1 text-[#63788E] hover:bg-neutral-100 hover:text-[#10253A] transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2 pt-3">
            {PRESET_OPTIONS.map((opt) => {
              const isSelected = tempPreset === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handlePresetClick(opt.id)}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors cursor-pointer ${
                    isSelected
                      ? "bg-[#E6F7F5] border border-[#15BBAE]/30 text-[#15BBAE] font-semibold"
                      : "bg-[#F8FAFC] border border-[#E2E8F0] text-[#63788E] font-medium hover:bg-[#F1F5F9] hover:text-[#10253A]"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Custom Date Inputs Section */}
          <div className="mt-3.5 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3.5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold tracking-wider text-[#63788E] uppercase mb-1.5">
                  FROM DATE
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={formatYmd(tempStart)}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className="w-full rounded-lg border border-[#E2E8F0] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#10253A] focus:border-[#15BBAE] focus:outline-none cursor-pointer"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-wider text-[#63788E] uppercase mb-1.5">
                  TO DATE
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={formatYmd(tempEnd)}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    className="w-full rounded-lg border border-[#E2E8F0] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#10253A] focus:border-[#15BBAE] focus:outline-none cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Footer Actions */}
          <div className="mt-3.5 pt-3 border-t border-[#F0F4F8] flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium text-[#63788E] truncate">
              {formatDateDisplay(tempStart)} — {formatDateDisplay(tempEnd)}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs font-semibold text-[#475569] hover:bg-neutral-50 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="rounded-lg bg-[#15BBAE] hover:bg-[#13a89d] px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Check size={14} className="stroke-[2.5]" />
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
